/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useState } from 'react';
import {
  Col,
  colors,
  Container,
  mediaQueries,
  Row,
  size,
} from '@everlywell/leaves';
import { animated as A, useSpring, config } from 'react-spring';
import { sanitize } from 'utils/domHelpers';

import SectionWrapper from 'components/SectionWrapper';
import useMediaQuery from 'utils/useMediaQuery';
import { useScrolledIntoViewOnce } from 'utils/hooks';
import * as S from './styles';

export interface TestimonialsSectionProps {
  ctaText?: string;
  ctaUrl?: string;
  description: {
    description: string;
  };
  videoLink: string;
}

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = props => {
  const { ctaUrl, ctaText, description, videoLink } = props;

  // state for triggering animation springs
  const [callToActionInView, setCallToActionInView] = useState<boolean>(false);
  const [videoInView, setVideoInView] = useState(false);

  const testimonialVideoEl = useScrolledIntoViewOnce(() => {
    setVideoInView(true);
  });

  const isMobile = useMediaQuery(mediaQueries.forPhoneOnly);

  // Video covers up section wrapper background and sits flush against above content on mobile
  const mobileWrapperMargin = isMobile ? `${size.xl1}px` : 0;

  const callToActionRef = ctaText
    ? useScrolledIntoViewOnce(() => {
        setCallToActionInView(true);
      })
    : undefined;

  // triggered from scrolling into view
  const headlineTransition = useSpring({
    opacity: videoInView ? 1 : 0,
    transform: videoInView ? 'translateY(0)' : `translateY(${size.xl2}px)`,
    config: config.slow,
  });

  // triggered from scrolling into view
  const callToActionTransition = useSpring({
    opacity: callToActionInView ? 1 : 0,
    transform: callToActionInView
      ? 'translateY(0)'
      : `translateY(${size.xl2}px)`,
    config: config.slow,
  });
  return (
    <SectionWrapper
      ref={testimonialVideoEl}
      style={{ backgroundColor: colors.teal1, marginTop: mobileWrapperMargin }}
    >
      {videoInView ? (
        <>
          <S.MobileVideoContainer>
            <S.Video
              data-testid="testimonial-video"
              aria-hidden="true"
              src={videoLink}
              width={560}
              height={315}
              allow="autoplay; fullscreen"
            />
          </S.MobileVideoContainer>
          <Container>
            <Row middle="xs">
              {/* Reverse columns on mobile */}
              <Col last="xs" first="md" xs={12} md={5}>
                <Row center="xs" start="md">
                  <Col xs={12}>
                    <Row center="xs" start="md">
                      <div>
                        {/* @ts-ignore */}
                        <A.div style={headlineTransition}>
                          <S.Headline>
                            <span
                              dangerouslySetInnerHTML={{
                                __html: sanitize(description.description),
                              }}
                            />
                          </S.Headline>
                        </A.div>
                      </div>
                    </Row>
                  </Col>
                  {ctaText ? (
                    <Col xs={12}>
                      <Row center="xs" start="md">
                        <div ref={callToActionRef}>
                          {/* @ts-ignore */}
                          <A.div style={callToActionTransition}>
                            <S.CTA appearance="secondary" href={ctaUrl}>
                              {ctaText}
                            </S.CTA>
                          </A.div>
                        </div>
                      </Row>
                    </Col>
                  ) : null}
                </Row>
              </Col>
              <Col xs={12} md={7}>
                <S.VideoContainer>
                  <S.Video
                    data-testid="testimonial-video"
                    aria-hidden="true"
                    src={videoLink}
                    width={560}
                    height={315}
                    allow="autoplay; fullscreen"
                  />
                </S.VideoContainer>
              </Col>
            </Row>
          </Container>
        </>
      ) : null}
    </SectionWrapper>
  );
};

export default TestimonialsSection;
